import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import { inputClasses } from "@mui/material/Input";
import InputAdornment from "@mui/material/InputAdornment";
import { inputLabelClasses } from "@mui/material/InputLabel";
import Typography from "@mui/material/Typography";
import { DatePicker as MuiDatePicker } from "@mui/x-date-pickers/DatePicker";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { Messages } from "common/components/Elements/ExpandableMessages/ExpandableMessages";
import { hasContentForMessages } from "common/components/Elements/ExpandableMessages/utils.ts";
import InputAdormentLoading from "common/components/Elements/Form/InputAdormentLoading/InputAdormentLoading";
import InputDynamicLabel from "common/components/Elements/Form/InputDynamicLabel/InputDynamicLabel";
import TimeZonePicker from "common/components/Elements/Form/TimeZonePicker/TimeZonePicker";
import { TimeZoneIcon } from "common/components/Elements/Icon/Icon";
import SimpleModal from "common/components/Elements/Modal/SimpleModal/SimpleModal";
import { generateRandomString } from "common/utilities/core/string";
import * as I from "core/api/coreApiClient";
import ClientConstraintViolationTarget from "core/components/Elements/ValueEdit/ClientConstraintViolationTarget";
import type { ConfigurationState } from "core/redux/reducers/configuration/configurationReducer.types";
import type { ApplicationState } from "core/redux/reducers/rootReducer";
import {
  apiDateFormat,
  configurationFormatPatternDateEdit,
  configurationFormatPatternDateTimeEdit,
  getTimeZoneGroup,
  isValidDate,
  toDateWithGivenTimeZone,
  toISOStringWithoutTimeZone,
} from "core/utilities/dateFormat/dateFormat";
import { inputStyles, inTableInputMarginTop, inTableInputMinWidth } from "core/utilities/styles/input.styles";
import theme, { sxClassName } from "core/utilities/theme";
import { format as formatDate, parseISO } from "date-fns";
import I18n from "i18n/en-US";
import React from "react";
import { connect } from "react-redux";

/**
 * Format ISO date(time) to given pattern
 */
const getFormat = (configuration: I.AppConfiguration, format?: string, enableTime?: boolean) =>
  format ||
  (enableTime
    ? configurationFormatPatternDateTimeEdit(configuration.format_pattern_date)
    : configurationFormatPatternDateEdit(configuration.format_pattern_date));

/**
 * Format required by the API
 */
const formatToISO = (configuration: I.AppConfiguration, date: Date, hasTime?: boolean) =>
  hasTime ? date.toISOString() : formatDate(date, apiDateFormat);

interface PropsFromState {
  configuration: ConfigurationState;
}

/** Date when datetime, string when date only */
type ValueWithoutTimeZone = Date | string | null;

export interface ComponentProps extends PropsFromState {
  value?: string;
  label?: string;
  title?: string;
  required?: boolean;
  disabled?: boolean;
  onChange: (newDate?: string) => void;
  errorMsg?: I.Message[];
  enableTime?: boolean;
  format?: string;
  e2eId?: string;
  fullWidth?: boolean;
  longFormStyle?: boolean;
  clearable?: boolean;
  minDate?: string;
  hideTimeZoneText?: boolean;
  errorMsgPreventSubmit?: boolean;
  sideLabel?: boolean;
  loading?: boolean;
  helpInfo?: I.Denotation;
  inTable?: boolean;
}

interface ComponentState {
  valueWithoutTimeZone: ValueWithoutTimeZone;
  id: string;
  timeZone: string;
  displayFormat: string;
  openModal: boolean;
  browserTimeZone: string;
  showPicker: boolean;
  focus: boolean;
  invalidChange: boolean;
}

export class DatePicker extends React.Component<ComponentProps, ComponentState> {
  constructor(props: ComponentProps) {
    super(props);

    const { configuration, timeZone } = props.configuration;

    this.state = {
      id: generateRandomString(),
      valueWithoutTimeZone: null,
      timeZone,
      displayFormat: getFormat(configuration, props.format, props.enableTime),
      openModal: false,
      browserTimeZone: timeZone,
      showPicker: false,
      focus: false,
      invalidChange: false,
    };
  }

  componentDidMount() {
    this.setValueWithoutTimeZone();
  }

  componentDidUpdate(prevProps: ComponentProps, prevState: ComponentState) {
    const { timeZone } = this.state;
    const { value } = this.props;

    if (timeZone !== prevState.timeZone || value !== prevProps.value) {
      this.setValueWithoutTimeZone();
    }
  }

  setValueWithoutTimeZone() {
    const { timeZone, displayFormat } = this.state;
    const { value, enableTime } = this.props;

    const formatValueIfValid = (val: string) =>
      isValidDate(displayFormat, val) ? toISOStringWithoutTimeZone(val, timeZone) : val;

    let valueWithoutTimeZone: ValueWithoutTimeZone = (enableTime && value ? formatValueIfValid(value) : value) || null;

    // Transform the final date (with no time, i.e. 2010-10-09) value to a Date object
    // using the browser's timezone so there is no more transformation made by the Mui component
    if (!enableTime && typeof value === "string") valueWithoutTimeZone = parseISO(value);

    this.setState({
      valueWithoutTimeZone,
      invalidChange: false,
    });
  }

  render() {
    const {
      onChange,
      disabled,
      label,
      required,
      errorMsg: errorMsgProp,
      enableTime,
      e2eId,
      fullWidth,
      longFormStyle,
      clearable: clearableProp,
      minDate,
      hideTimeZoneText,
      configuration,
      sideLabel,
      errorMsgPreventSubmit,
      loading,
      title,
      helpInfo,
      inTable,
    } = this.props;
    const {
      valueWithoutTimeZone,
      timeZone,
      displayFormat,
      openModal,
      browserTimeZone,
      showPicker,
      focus,
      id,
      invalidChange,
    } = this.state;

    const browserTimeZoneText = (getTimeZoneGroup(browserTimeZone) || { text: browserTimeZone }).text;
    const timeZoneText = (getTimeZoneGroup(timeZone) || { text: timeZone }).text;

    const ComponentPicker = enableTime ? DateTimePicker : MuiDatePicker;

    /**
     * Ensure the data format is valid.
     * On blur, show an error if the user started to enter a new data but didn't finish it / is not valid.
     */
    const invalid =
      (valueWithoutTimeZone !== null && !isValidDate(displayFormat, valueWithoutTimeZone)) || (!focus && invalidChange);

    const errorMsg = [...(errorMsgProp || [])];
    if (invalid) {
      const invalidErrorMsg = I18n.Elements.Form.DatePicker.invalid(displayFormat.toUpperCase());
      errorMsg.unshift({ severity: I.SeverityConstants.ERROR, text: invalidErrorMsg });
    }

    const clearable = valueWithoutTimeZone !== null && disabled !== true && clearableProp === true;

    /**
     * The Mui DateTimePicker component only takes a Date as value, so we need to convert back our value without timezone
     * to a Date object which will use the local (browser) timezone by default
     */
    const valueForMuiComponent: Date | null =
      typeof valueWithoutTimeZone === "string" ? new Date(valueWithoutTimeZone) : valueWithoutTimeZone;

    // TZ picker is disabled, see SPAUX-197
    const enableTimeZonePicker = false;

    let inputEndAdornment = (
      <InputAdornment position="end">
        {clearable && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              onChange();
            }}
            data-testid="reset"
            size="small"
          >
            <ClearIcon style={{ fontSize: "16px" }} />
          </IconButton>
        )}
      </InputAdornment>
    );

    if (loading) {
      inputEndAdornment = <InputAdormentLoading />;
    }

    const styles = inputStyles({
      longFormStyle,
      sideLabel,
      errorMsg,
      inTable,
      minWidth: enableTime ? inTableInputMinWidth.DatePickerWithTime : inTableInputMinWidth.DatePicker,
    });

    return (
      <InputDynamicLabel
        sideLabel={sideLabel}
        htmlFor={id}
        disabled={disabled}
        errorMsg={errorMsg}
        label={label || ""}
        required={required}
        title={title}
      >
        <ClientConstraintViolationTarget violated={invalid} preventSubmit={errorMsgPreventSubmit || invalid || false}>
          <Box
            sx={{
              position: "relative",
              width: "100%",
              marginTop: inTable ? `${inTableInputMarginTop.DatePicker}px` : undefined,
            }}
          >
            {openModal && (
              <SimpleModal
                open
                title={I18n.Elements.Form.DatePicker.modalTitle}
                onClose={() => this.setState({ openModal: false })}
                content={
                  <TimeZonePicker
                    value={timeZone}
                    onChange={(newTimeZone) => {
                      this.setState({ timeZone: newTimeZone, openModal: false });
                    }}
                  />
                }
              />
            )}
            <ComponentPicker
              label={sideLabel ? undefined : label}
              value={valueForMuiComponent}
              format={displayFormat}
              views={
                enableTime
                  ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    (["year", "month", "day", "hours", "minutes", "seconds"] as any)
                  : ["year", "month", "day"]
              }
              disabled={disabled}
              sx={styles.root}
              slots={{
                // eslint-disable-next-line react/no-unstable-nested-components
                inputAdornment: () => inputEndAdornment,
              }}
              slotProps={{
                textField: {
                  id,
                  title,
                  margin: "dense",
                  variant: "standard",
                  helperText: hasContentForMessages(errorMsg, helpInfo) ? (
                    <Messages messages={errorMsg} denotation={helpInfo} />
                  ) : undefined,
                  error: errorMsg && errorMsg.length > 0,
                  onFocus: () => this.setState({ focus: true }),
                  onBlur: () => this.setState({ focus: false }),
                  fullWidth,
                  required,
                  InputLabelProps: {
                    htmlFor: id,
                    sx: {
                      [sxClassName(inputLabelClasses.root)]: styles.labelRoot,
                      [sxClassName(inputLabelClasses.disabled)]: styles.labelDisabled,
                      [sxClassName(inputLabelClasses.shrink)]: styles.labelShrink,
                    },
                  },
                  InputProps: {
                    sx: {
                      [sxClassName(inputClasses.root)]: inTable ? styles.inputRoot : styles.shortInputRoot,
                    },
                    // endAdornment is overriden by the component props, we use slots.inputAdornment to handle it
                    startAdornment: (
                      <InputAdornment position="start">
                        {enableTime && (
                          <IconButton
                            disabled={disabled}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (enableTimeZonePicker) this.setState({ openModal: true });
                            }}
                            size="small"
                            title={timeZoneText}
                            style={{
                              color:
                                browserTimeZoneText !== timeZoneText
                                  ? theme.palette.primary.main
                                  : theme.palette.text.primary,
                              cursor: disabled || !enableTimeZonePicker ? "default" : "pointer",
                            }}
                            disableRipple={disabled || !enableTimeZonePicker}
                            data-testid="timeZone"
                          >
                            <Box component="span" mt={-0.5} mr={0.5}>
                              <TimeZoneIcon />
                            </Box>
                            {!hideTimeZoneText && browserTimeZoneText !== timeZoneText && (
                              <Box component="span" mt={-0.5} mr={1}>
                                <Typography variant="body2" style={{ whiteSpace: "nowrap" }}>
                                  {browserTimeZoneText !== timeZoneText ? `${timeZoneText.slice(0, 30)}...` : ""}
                                </Typography>
                              </Box>
                            )}
                          </IconButton>
                        )}
                        <Box mt={-0.5}>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => this.setState({ showPicker: true })}
                            data-testid="calendar"
                            aria-label="Open date picker"
                          >
                            <CalendarTodayIcon style={{ fontSize: "16px" }} />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  },
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  ...({ "data-e2e-form-datepicker": e2eId } as any),
                },
              }}
              minDate={minDate ? new Date(minDate) : undefined}
              open={showPicker}
              // Set to 24 hours clock
              ampm={enableTime ? false : undefined}
              onClose={() => this.setState({ showPicker: false })}
              onChange={(newValue, { validationError }) => {
                if (!newValue) {
                  onChange(undefined);
                } else if (validationError) {
                  // The user is currently writing the data, validation based on the format is built-in the Mui component
                  this.setState({ invalidChange: true });
                } else {
                  // Wrap in settimeout to avoid rendering concurrency issues between the Mui component
                  // and the controlled value we set
                  setTimeout(() => {
                    // When datetime: the output value is always formatted based on the local timezone (browser).
                    // We give that value (datetime with offset) as the "source timezone".
                    onChange(
                      enableTime
                        ? formatToISO(
                            configuration.configuration,
                            toDateWithGivenTimeZone(newValue as unknown as Date, browserTimeZone, timeZone),
                            true,
                          )
                        : formatToISO(configuration.configuration, newValue as unknown as Date, false),
                    );
                  }, 0);
                }
              }}
            />
          </Box>
        </ClientConstraintViolationTarget>
      </InputDynamicLabel>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  configuration: state.configuration,
});

export default connect(mapStateToProps)(DatePicker);
